import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Save, AlertTriangle, ClipboardList } from 'lucide-react';
import { warehouseAPI } from '../../api/warehouses';
import { customerAPI } from '../../api/customers';
import { itemAPI } from '../../api/items';
import { WarehouseItemsTable, WarehouseItemRow } from '../../components/warehouse/WarehouseItemsTable';
import { Warehouse, Customer, Item, WarehouseInventory } from '../../types/entities';
import toast from 'react-hot-toast';

interface ManualIssueFormData {
  warehouseId: number;
  transactionDate: string;
  customerId?: number;
  notes?: string;
}

export const ManualIssuePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const warehouseIdParam = searchParams.get('warehouseId');

  const [loading, setLoading] = useState(false);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [catalogItems, setCatalogItems] = useState<Item[]>([]);
  const [inventory, setInventory] = useState<WarehouseInventory[]>([]);
  const [items, setItems] = useState<WarehouseItemRow[]>([
    { id: 'temp-1', itemId: null, itemName: '', quantity: 0, unit: 'M3' },
  ]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ManualIssueFormData>({
    defaultValues: {
      warehouseId: warehouseIdParam ? parseInt(warehouseIdParam) : undefined,
      transactionDate: new Date().toISOString().split('T')[0],
    },
  });

  const selectedWarehouseId = watch('warehouseId');

  useEffect(() => {
    loadWarehouses();
    loadCustomers();
    loadCatalogItems();
  }, []);

  // Load inventory when warehouse changes
  useEffect(() => {
    if (selectedWarehouseId) {
      loadInventory(Number(selectedWarehouseId));
    } else {
      setInventory([]);
    }
  }, [selectedWarehouseId]);

  const loadWarehouses = async () => {
    try {
      const data = await warehouseAPI.getAll({ status: 'ACTIVE' });
      setWarehouses(data);
    } catch {
      toast.error(t('warehouses.loadError'));
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await customerAPI.getAll();
      setCustomers(data.filter((c) => c.status === 'ACTIVE'));
    } catch {
      toast.error(t('customers.loadError'));
    }
  };

  const loadCatalogItems = async () => {
    try {
      const data = await itemAPI.getAll();
      setCatalogItems(data.filter((i) => i.status === 'ACTIVE'));
    } catch {
      toast.error(t('items.loadError'));
    }
  };

  const loadInventory = async (warehouseId: number) => {
    setInventoryLoading(true);
    try {
      const result = await warehouseAPI.getFilteredInventory(warehouseId, { pageSize: 1000 });
      setInventory(result.data);
    } catch {
      setInventory([]);
    } finally {
      setInventoryLoading(false);
    }
  };

  // Build stock map for quick lookup
  const stockMap = useMemo(() => {
    const map: Record<number, number> = {};
    inventory.forEach((inv) => {
      map[inv.itemId] = inv.currentStock;
    });
    return map;
  }, [inventory]);

  // Compute insufficient stock warnings
  const insufficientItems = useMemo(() => {
    return items.filter((item) => {
      if (!item.itemId || item.quantity <= 0) return false;
      const stock = stockMap[item.itemId];
      return stock !== undefined && item.quantity > stock;
    });
  }, [items, stockMap]);

  const onSubmit = async (data: ManualIssueFormData) => {
    if (items.length === 0) {
      toast.error(t('warehouse.manualIssue.itemsRequired'));
      return;
    }

    const hasInvalidItems = items.some((item) => !item.itemId || item.quantity <= 0);
    if (hasInvalidItems) {
      toast.error(t('warehouse.manualIssue.invalidItems'));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        transactionDate: data.transactionDate,
        customerId: data.customerId || undefined,
        notes: data.notes || undefined,
        items: items.map((item) => ({
          itemId: item.itemId!,
          itemName: item.itemName,
          quantity: item.quantity,
          unit: item.unit,
        })),
      };

      await warehouseAPI.manualIssue(data.warehouseId, payload);
      toast.success(t('warehouse.manualIssue.success'));
      navigate(`/warehouses/${data.warehouseId}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('warehouse.manualIssue.title')}
          </h1>
          <p className="text-gray-500 text-sm mt-1">{t('warehouse.manualIssue.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedWarehouseId && (
            <Link
              to={`/warehouses/audit-logs?warehouseId=${selectedWarehouseId}`}
              className="btn-secondary flex items-center gap-2 text-sm"
            >
              <ClipboardList className="w-4 h-4" />
              {t('nav.auditLogs')}
            </Link>
          )}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </button>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Section: Thông tin cơ bản */}
        <div className="card space-y-5">
          <h2 className="text-base font-semibold text-gray-800 border-b pb-3">
            {t('warehouse.manualIssue.basicInfo')}
          </h2>

          {/* Row 1: Kho + Ngày xuất */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.manualIssue.warehouse')} <span className="text-red-500">*</span>
              </label>
              <select
                {...register('warehouseId', { required: true, valueAsNumber: true })}
                className={`input-field w-full ${errors.warehouseId ? 'border-red-300' : ''}`}
              >
                <option value="">{t('warehouse.manualIssue.selectWarehouse')}</option>
                {warehouses.map((wh) => (
                  <option key={wh.id} value={wh.id}>
                    {wh.name} ({wh.code})
                  </option>
                ))}
              </select>
              {errors.warehouseId && (
                <p className="text-red-500 text-xs mt-1">
                  {t('warehouse.manualIssue.warehouseRequired')}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.manualIssue.transactionDate')} <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                {...register('transactionDate', { required: true })}
                className={`input-field w-full ${errors.transactionDate ? 'border-red-300' : ''}`}
              />
            </div>
          </div>

          {/* Khách hàng */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('warehouse.manualIssue.customer')}
            </label>
            <select
              {...register('customerId', { valueAsNumber: true })}
              className="input-field w-full"
            >
              <option value="">{t('warehouse.manualIssue.customerPlaceholder')}</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Section: Danh sách hàng hóa */}
        <div className="card space-y-4">
          <div className="flex items-center justify-between border-b pb-3">
            <h2 className="text-base font-semibold text-gray-800">
              {t('warehouse.manualIssue.items')} <span className="text-red-500">*</span>
            </h2>
            {inventoryLoading && (
              <span className="text-xs text-gray-400">{t('common.loading')}</span>
            )}
          </div>

          <WarehouseItemsTable
            items={items}
            onChange={setItems}
            catalogItems={catalogItems}
            showCurrentStock={true}
            inventory={inventory}
          />

          {/* Cảnh báo tồn kho không đủ */}
          {insufficientItems.length > 0 && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-800">
                    {t('warehouse.manualIssue.insufficientStockTitle')}
                  </p>
                  <ul className="mt-1.5 space-y-0.5">
                    {insufficientItems.map((item) => {
                      const stock = item.itemId ? (stockMap[item.itemId] ?? 0) : 0;
                      return (
                        <li key={item.id} className="text-xs text-red-700">
                          <span className="font-medium">{item.itemName}</span>
                          {': '}
                          {t('warehouse.manualIssue.insufficientStockDetail', {
                            issue: item.quantity,
                            stock,
                          })}
                        </li>
                      );
                    })}
                  </ul>
                  <p className="text-xs text-red-600 mt-2">
                    {t('warehouse.manualIssue.lowStockNote')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Cảnh báo chung khi chưa chọn kho */}
          {!selectedWarehouseId && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-800">
                {t('warehouse.manualIssue.selectWarehouseFirst')}
              </p>
            </div>
          )}
        </div>

        {/* Section: Ghi chú */}
        <div className="card">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('warehouse.manualIssue.notes')}
          </label>
          <textarea
            {...register('notes')}
            rows={3}
            className="input-field w-full"
            placeholder={t('warehouse.manualIssue.notesPlaceholder')}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="btn-primary flex items-center gap-2"
            disabled={loading}
          >
            <Save className="w-4 h-4" />
            {loading ? t('common.loading') : t('warehouse.manualIssue.submit')}
          </button>
        </div>
      </form>
    </div>
  );
};
